import { useEffect, useState } from 'react';
import { Dialog } from '@base-ui/react';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { api } from '../lib/api';

interface AssignableClassroom {
  id: string;
  name: string;
  level_id: string | null;
  active_student_count: number;
}

interface Props {
  documentId: string;
  docTitle: string;
  onClose: () => void;
  onSent: (studentCount: number) => void;
}

const overlayClass = "fixed inset-0 bg-black/45 flex items-end justify-center z-50 pt-[env(safe-area-inset-top)] min-[620px]:items-center";
const modalClass = "bg-card w-full max-w-[520px] max-h-[92dvh] overflow-y-auto rounded-t-[20px] pt-5 px-[18px] pb-[calc(20px+env(safe-area-inset-bottom))] flex flex-col gap-[14px] min-[620px]:rounded-[20px]";
const fieldLabelClass = "flex flex-col gap-[6px] text-[13px] font-bold text-muted-foreground";
const classItemClass = "flex items-center gap-[10px] px-3 py-[10px] border-[1.5px] border-border rounded-sm cursor-pointer min-h-[44px]";
const classItemCheckedClass = "border-naia-blue bg-[color-mix(in_srgb,var(--naia-blue)_8%,transparent)]";

export default function SendAssignmentModal({ documentId, docTitle, onClose, onSent }: Props) {
  const [classrooms, setClassrooms] = useState<AssignableClassroom[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dueAt, setDueAt] = useState('');
  const [format, setFormat] = useState<'closed' | 'interactive'>('closed');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [missingBaremes, setMissingBaremes] = useState(0);
  const [recopie, setRecopie] = useState(false);
  const [textes, setTextes] = useState<Array<{ id: string; niveau: 'college' | 'lycee'; titre: string; texte: string }>>([]);
  const [recopieTexteId, setRecopieTexteId] = useState('');

  useEffect(() => {
    api.assignments.assignableClassrooms
      .query({ documentId })
      .then((c) => setClassrooms(c as AssignableClassroom[]))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [documentId]);

  useEffect(() => {
    api.content.readiness
      .query({ id: documentId })
      .then((r) => setMissingBaremes((r as { missing_section_ids: string[] }).missing_section_ids.length))
      .catch(() => {
        /* readiness informative seulement */
      });
  }, [documentId]);

  useEffect(() => {
    if (!recopie || textes.length > 0) return;
    api.assignments.graphie.textes
      .query()
      .then((t) => {
        const list = t as typeof textes;
        setTextes(list);
        setRecopieTexteId((cur) => cur || list[0]?.id || '');
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [recopie, textes.length]);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const send = async () => {
    setSending(true);
    setError(null);
    try {
      const result = await api.assignments.send.mutate({
        documentId,
        classroomIds: [...selected],
        dueAt: dueAt ? new Date(dueAt).toISOString() : null,
        format,
        recopieTexteId: format === 'closed' && recopie ? recopieTexteId : null,
      });
      onSent(result.student_count);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSending(false);
    }
  };

  return (
    <Dialog.Root open onOpenChange={(o) => { if (!o) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Viewport className={overlayClass}>
          <Dialog.Popup className={modalClass} aria-label="Envoyer en devoir">
            <h2 className="m-0 text-[18px] font-extrabold">Envoyer en devoir</h2>
            <p className="m-0 text-[13px] text-muted-foreground">« {docTitle} » — sélectionnez les classes destinataires.</p>

            {error && <Alert>{error}</Alert>}

            {missingBaremes > 0 && (
              <Alert>
                ⚠️ {missingBaremes} section{missingBaremes > 1 ? 's' : ''} notée{missingBaremes > 1 ? 's' : ''} sans barème — la
                correction automatique ne pourra pas s'en servir. Vous pouvez tout de même envoyer.
              </Alert>
            )}

            {loading ? (
              <p className="text-muted-foreground">Chargement…</p>
            ) : classrooms.length === 0 ? (
              <p className="text-muted-foreground">Aucune classe ne vous est affectée dans cette école.</p>
            ) : (
              <div className="flex flex-col gap-2 max-h-[40dvh] overflow-y-auto">
                {classrooms.map((c) => {
                  const plural = c.active_student_count > 1 ? 's' : '';
                  return (
                    <label
                      key={c.id}
                      className={cn(classItemClass, selected.has(c.id) && classItemCheckedClass)}
                    >
                      <input type="checkbox" className="w-[18px] h-[18px] flex-none" checked={selected.has(c.id)} onChange={() => toggle(c.id)} />
                      <span className="flex-1 min-w-0">
                        <span className="font-bold">{c.name}</span>
                        <span
                          className={cn(
                            "text-[12px] text-muted-foreground",
                            c.active_student_count === 0 && "text-destructive",
                          )}
                        >
                          {' '}
                          · {c.active_student_count} élève{plural} actif{plural}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            )}

            <div className={fieldLabelClass}>
              Format
              <div className="flex gap-2 flex-wrap justify-start mt-1">
                <Button
                  variant={format === 'closed' ? 'default' : 'secondary'}
                  onClick={() => setFormat('closed')}
                >
                  📷 Rendu photo
                </Button>
                <Button
                  variant={format === 'interactive' ? 'default' : 'secondary'}
                  onClick={() => setFormat('interactive')}
                >
                  ✨ Parcours interactif
                </Button>
              </div>
            </div>

            {format === 'closed' && (
              <div className={fieldLabelClass}>
                <label className="flex items-center gap-[10px] text-[13px] font-bold text-foreground min-h-[44px] cursor-pointer">
                  <input type="checkbox" className="w-[18px] h-[18px] flex-none" checked={recopie} onChange={(e) => setRecopie(e.target.checked)} />
                  Recopie de référence (analyse de la graphie, sans note)
                </label>
                {recopie && (
                  <>
                    <NativeSelect aria-label="Texte de référence" value={recopieTexteId} onChange={(e) => setRecopieTexteId(e.target.value)}>
                      {textes.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.niveau === 'college' ? 'Collège' : 'Lycée'} — {t.titre}
                        </option>
                      ))}
                    </NativeSelect>
                    {textes.find((t) => t.id === recopieTexteId) && (
                      <p className="m-0 p-2 rounded-card bg-muted text-[12.5px] leading-relaxed text-foreground font-normal">
                        {textes.find((t) => t.id === recopieTexteId)!.texte}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}

            <label className={fieldLabelClass}>
              Date de remise (optionnel)
              <Input type="datetime-local" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
            </label>

            <div className="flex gap-[10px] justify-end flex-wrap">
              <Button variant="secondary" onClick={onClose}>Annuler</Button>
              <Button disabled={sending || selected.size === 0 || (format === 'closed' && recopie && !recopieTexteId)} onClick={() => void send()}>
                {sending ? 'Envoi…' : 'Envoyer'}
              </Button>
            </div>
          </Dialog.Popup>
        </Dialog.Viewport>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
